import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import { apiClient } from '../../components/api/ApiClient';
import JalaliDatePicker from '../../components/common/JalaliDatePicker';
import moment from 'moment-jalaali';

function CancellationInvoicesPage(mode) {
    const cancelMode = mode?.mode || mode;
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
    const navigate = useNavigate();

    const [inputErrors, setInputErrors] = useState({});
    const [lastInvoiceDate, setLastInvoiceDate] = useState();
    const [invoiceNum, setInvoiceNum] = useState(null);
    const [finalDate, setFinalDate] = useState(null);
    const [finalFaDateStr, setFinalFaDateStr] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showSave, setShowSave] = useState(false);

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => setToast({ ...toast, show: false }), 5000);
            return () => clearTimeout(timer);
        }
    }, [toast.show]);

    const showToast = (message, type = 'info') => setToast({ show: true, message, type });

    const parseToGregorianMoment = (input) => {
        if (!input) return null;
        if (typeof input !== 'string') {
            const m = moment(input);
            if (m.isValid()) return m;
            return null;
        }
        
        let m = moment(input, 'HH:mm YYYY/MM/DD', true);
        if (!m.isValid()) m = moment(input, 'YYYY/MM/DD HH:mm', true);
        if (!m.isValid()) m = moment(input, 'HH:mm jYYYY/jMM/jDD', true);
        if (!m.isValid()) m = moment(input, 'jYYYY/jMM/jDD HH:mm', true);
        
        if (!m.isValid()) {
            m = moment(input);
        }
        
        if (m.isValid()) {
            if (m.year() < 1800) {
                let mJ = moment(input, 'HH:mm jYYYY/jMM/jDD', true);
                if (!mJ.isValid()) mJ = moment(input, 'jYYYY/jMM/jDD HH:mm', true);
                if (mJ.isValid()) return mJ;
            }
            return m;
        }
        return null;
    };

    const handleDateChange = (value, fieldName) => {
        if (!value) return;
        const m = parseToGregorianMoment(value);
        if (m && m.isValid()) {
            const geDate = m.format("HH:mm YYYY/MM/DD");
            const faDateStr = m.format("HH:mm jYYYY/jMM/jDD");
            if (fieldName === "final") {
                setInputErrors(prev => ({ ...prev, finalDate: "" }));
                const now = moment();
                if (m.isAfter(now)) {
                    setInputErrors(prev => ({ ...prev, finalDate: 'تاریخ انتخاب شده نباید از زمان حال بزرگتر باشد!' }));
                    return;
                }
                if (lastInvoiceDate && m.isBefore(lastInvoiceDate)) {
                    setInputErrors(prev => ({ ...prev, finalDate: 'تاریخ انتخاب شده نباید از تاریخ اخرین صورتحساب کوچکتر باشد!' }));
                    return;
                }
                setFinalFaDateStr(faDateStr);
                setFinalDate(geDate);
            }
        }
    };

    const validateInvoiceFields = () => {
        const errors = {};
        ['invoiceNum', 'finalDate'].forEach(field => {
            const val = field === 'invoiceNum' ? invoiceNum : finalDate;
            if (val === null || (typeof val === 'string' && val.trim() === ""))
                errors[field] = "این فیلد الزامی است";
        });
        setInputErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const sendInvoice = async () => {
        if (!validateInvoiceFields()) {
            handleCloseSave();
            showToast('فیلد های الزامی را پر کنید !', 'error');
            return;
        }
        try {
            await apiClient('/invoice/api/invoice-cancellation/', { method: 'POST', data: { id, invoiceNum, finalDate } });
            showToast('صورتحساب شما با موفقیت ثبت شد.', 'success');
            navigate('/invoices/list/');
        } catch (e) {
            if (e.message) {
                if (e.message === 'referenced') {
                    showToast('این صورتحساب قبلاً مرجع فاکتور دیگری قرار گرفته است.', 'error');
                } else {
                    showToast(e.message, 'error');
                }
            } else {
                showToast('خطایی رخ داده است. لطفاً مجدداً تلاش کنید.', 'error');
            }
        }
    };

    const getLastEndDate = async () => {
        setLoading(true);
        try {
            const response = await apiClient(`/invoice/api/invoice-cancellation/?target=${id}`, { method: 'GET' });
            if (response) {
                setInvoiceNum(response.num);
                const refDateMoment = parseToGregorianMoment(response.date);
                if (refDateMoment && refDateMoment.isValid()) {
                    setLastInvoiceDate(refDateMoment);
                }
                const now = moment();
                setFinalDate(now.format('HH:mm YYYY/MM/DD'));
                setFinalFaDateStr(now.format('HH:mm jYYYY/jMM/jDD'));
            }
        } catch {
            showToast('مشکلی در دریافت اطلاعات رخ داده است. لطفاً دوباره تلاش کنید.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getInvoice = async () => {
        try {
            setLoading(true);
            const response = await apiClient('/invoice/api/', { data: { invoiceId: id }, method: 'POST' });
            const inv = response.data;
            if (inv) {
                setInvoice({ ...inv, formattedFinalDate: inv.finalDate });
                const refDateMoment = parseToGregorianMoment(inv.finalDate);
                if (refDateMoment && refDateMoment.isValid()) {
                    setLastInvoiceDate(refDateMoment);
                }
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getInvoice();
        if (id) getLastEndDate();
    }, []);

    const handleCloseConfirm = () => setShowConfirm(false);
    const handleCloseSave = () => setShowSave(false);

    return (
        <div className="row">
            {toast.show && (
                <div aria-live="polite" aria-atomic="true" className="position-fixed w-100 p-3" style={{ zIndex: 1060, top: 0, right: 0 }}>
                    <div className="toast-container position-absolute p-3" style={{ top: 0, left: 0 }}>
                        <div className="toast show" role="alert" aria-live="assertive" aria-atomic="true">
                            <div className="toast-header" style={{ direction: 'rtl' }}>
                                <img src="/assets/images/redesigned.png" className="rounded ms-2" alt="..." width="20" />
                                <strong className="me-auto ms-2">پیام سیستم</strong>
                                <button type="button" className="btn-close" onClick={() => setToast({ ...toast, show: false })}></button>
                            </div>
                            <div className="toast-body">{toast.message}</div>
                        </div>
                    </div>
                </div>
            )}
            <div className="col-12 col-lg-12">
                <div className="card">
                    <div className="card-header d-lg-flex align-items-center justify-content-between bg-white">
                        <div className="card-title d-flex align-items-center gap-2">
                            <i className="far fa-file-alt font-18" />
                            <span>صدور صورتحساب فروش کالا و خدمات {cancelMode ? 'ابطالی' : 'اصلاحی'}</span>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="mb-5 mt-4 alert alert-warning" role="alert">
                                <p className="fw-bold mb-2">
                                    چنانچه پس از صدور صورتحساب (اصلی، اصلاحی، برگشت از فروش) شرط/شروط زیر برقرار باشد، صادرکننده صورتحساب الکترونیکی می‌بایست صورتحساب ابطالی را صادر و در سامانه مؤدیان ثبت نماید:
                                </p>
                                <ul className="mb-3">
                                    <li>تغییر اقلام اطلاعاتی مربوط به خریدار</li>
                                    <li>بازگشت تمام اقلام موجود در صورتحساب مرجع</li>
                                    <li>احراز عدم تحقق معامله بر اساس اسناد و مدارک مثبته</li>
                                    <li>اشتباه در ثبت تاریخ معامله</li>
                                </ul>
                                <hr />
                                <p className="mb-0 fw-bold">
                                    نکته: در صورتی که صورتحساب مرجع از طریق صورتحساب ابطالی باطل شود، صورتحساب‌های اصلاحی/برگشت از فروش تایید نشده آن (در صورت وجود) باطل می‌شوند.
                                </p>
                            </div>
                            <div>
                                <div className="d-flex mb-4 justify-item-center">
                                    <h6 className="mb-0 ms-5">صورتحساب مورد نظر</h6>
                                    <div className="bg-black flex-grow-1 m-auto me-5 header-hr"></div>
                                </div>
                                <div className='mt-2 mb-4 table-responsive'>
                                    {loading ? (
                                        <div className="text-center p-5 bg-secondary-subtle border-7">
                                            <p className='fs-2'>در حال بارگذاری داده‌ها...</p>
                                        </div>
                                    ) : invoice ? (
                                        <table className='table table-hover'>
                                            <thead>
                                                <tr>
                                                    <th className='text-center'>شماره صورتحساب</th>
                                                    <th className='text-center'>تاریخ و زمان صدور</th>
                                                    <th className='text-center'>مبلغ فروش</th>
                                                    <th className='text-center'>مبلغ ارزش افزوده</th>
                                                    {['3', '2'].includes(invoice.buyerType) && <th className='text-center'>شماره اقتصادی خریدار</th>}
                                                    {invoice.buyerType === '1' && <th className="text-center">شناسنامه / کد ملی خریدار</th>}
                                                    {invoice.buyerType === '4' && <th className="text-center">کد فراگیر اتباع غیر ایرانی خریدار</th>}<th className='text-center'>عملیات</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className='text-center'>{invoice.invoiceNum}</td>
                                                    <td className='text-center'>{invoice.formattedFinalDate}</td>
                                                    <td className='text-center'>{invoice.sumTotalPrice}</td>
                                                    <td className='text-center'>{invoice.sumTaxPrice}</td>
                                                    {['3', '2'].includes(invoice.buyerType) && <td className='text-center'>{invoice.buyerEconomicNum}</td>}
                                                    {['1', '4'].includes(invoice.buyerType) && <td className='text-center'>{invoice.buyerId}</td>}
                                                    <td>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <button className='btn btn-sm btn-link mt-2' onClick={() => navigate(`/invoice/view/${id}`)}>مشاهده کامل</button>
                </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className='text-center p-5 bg-secondary-subtle border-7'>
                                            <p className='fs-2'>هیچ صورتحسابی موجود نیست.</p>
                                        </div>
                                    )}
                                </div>
                            <div className="col-12">
                                <label className="font-weight-500 mb-1">شماره فاکتور</label>
                                <input
                                    type="number"
                                    className={`form-control form-control-sm ${inputErrors.invoiceNum ? 'is-invalid' : ''}`}
                                    value={invoiceNum || ''}
                                    disabled={true}
                                    onChange={(e) => setInvoiceNum(e.target.value)}
                                />
                                {inputErrors.invoiceNum && <div className="invalid-feedback">{inputErrors.invoiceNum}</div>}
                            </div>
                            <div className="col-12 mt-3">
                                <label className="font-weight-500 mb-1">
                                    تاریخ و ساعت صدور صورتحساب {cancelMode ? 'ابطالی' : 'اصلاحی'}
                                </label>
                                <JalaliDatePicker
                                    value={finalFaDateStr || ''}
                                    onChange={(value) => handleDateChange(value, "final")}
                                    showTime={true}
                                    placeholder="تاریخ را انتخاب کنید"
                                />
                                {inputErrors.finalDate && <div className="text-danger small mt-1">{inputErrors.finalDate}</div>}
                            </div>
                            <div className="mt-4 d-flex justify-content-start">
                                <button className="btn btn-secondary btn-sm me-2" onClick={() => setShowConfirm(true)}>بازگشت</button>
                                <button className="btn btn-primary btn-sm" onClick={() => setShowSave(true)}>ثبت صورتحساب</button>
                            </div>
                        </div>
                    </div>
                </div>

                <Modal show={showConfirm} onHide={handleCloseConfirm} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>بازگشت</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>آیا مطمئن هستید که می‌خواهید بازگردید؟ تغییرات ذخیره نخواهند شد.</Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-secondary" onClick={handleCloseConfirm}>لغو</button>
                        <button className="btn btn-primary" onClick={() => { setShowConfirm(false); navigate(-1); }}>بله، بازگشت</button>
                    </Modal.Footer>
                </Modal>

                <Modal show={showSave} onHide={handleCloseSave} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>ثبت صورتحساب</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>آیا از ثبت این صورتحساب اطمینان دارید؟</Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-secondary" onClick={() => { setInvoice(null); setShowSave(false); navigate(-1); }}>بازگشت بدون ثبت</button>
                        <button className="btn btn-primary" onClick={() => { setShowSave(false); sendInvoice(); }}>ثبت</button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
        </div>  
    );
}

export default CancellationInvoicesPage;
